import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  UsersIcon,
  DocumentTextIcon,
  ShareIcon,
  BuildingStorefrontIcon,
  BuildingLibraryIcon,
  UserPlusIcon,
  PlusIcon,
  CurrencyRupeeIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  Cog6ToothIcon,
  SignalIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  PauseIcon,
  PlayIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api, { getErrorMessage } from '@/services/api';
import { BackButton, SearchableSelect, EntityLink } from '@/components/common';
import type { InsurerAPIConfig } from '@/types/admin';
import { HospitalNetworkLink } from './HospitalNetworkLink';
import { formatCurrency, formatNumber } from '@/utils';

interface UserInfo {
  name: string;
  email?: string;
}

interface CompanyUser {
  id: string;
  user_info: UserInfo;
  role: string;
  is_active: boolean;
  // Item 3c: the `InsurerEmployee` row backing this membership, if one
  // exists — what the "Remove" action calls `DELETE /insurers/employees/`
  // with. `null` means the #282 heal has not (yet) produced one; the action
  // is disabled rather than pointed at nothing.
  employee_id: string | null;
}

interface RecentPolicy {
  id: string;
  name: string;
  category: string;
  status: string;
}

// Item 3a: `total_business` used to render as a fabricated `0`. The backend
// now reports the insurer's real sold book, same shape as the broker
// equivalent (`BrokerViewSet.summary`) so the two screens never disagree on
// what "sold" means.
interface TotalBusiness {
  policies_sold: number;
  premium_collected: number | string;
}

interface CompanySummary {
  id: string;
  name: string;
  users_count: number;
  policies_count: number;
  channels_count: number;
  active_brokers: number;
  total_business: TotalBusiness;
  users: CompanyUser[];
  recent_policies: RecentPolicy[];
}

// Item 3a: the full company record — Company Name, Company Code, Legal Name,
// Short Name, Email, Phone, Website, Category, IRDAI Registration Number,
// Country Tag, Network Size, Network Label and the Logo. Every one of these
// already exists on `InsurerDetailSerializer`; this page previously only ever
// fetched the stripped-down `summary/` action, so none of it ever reached the
// screen. Fields are optional because a partially-provisioned insurer may
// genuinely have some of them blank — the header renders each conditionally
// rather than showing an empty string as if it were a real value.
interface CompanyDetail {
  id: string;
  name: string;
  code: string;
  legal_name: string;
  short_name: string;
  email: string;
  phone: string;
  website: string;
  category: string;
  category_display: string;
  irdai_registration_number: string;
  country_name: string;
  network_size: number | null;
  network_label: string;
  logo_display_url: string;
  logo_url: string;
}

interface BrokerRelation {
  id: string;
  name: string;
  code: string;
  relation_status: string;
  relation_id: string | null;
}

interface Policy {
  id: string;
  name: string;
  code: string;
  category: string;
  category_ref?: string;
  category_name?: string;
  short_description?: string;
  is_active: boolean;
  is_visible_to_all: boolean;
  description?: string;
}

interface CreateUserFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  role: string;
}

interface CommissionRate {
  id: string;
  policy: string;
  policy_name: string;
  broker: string | null;
  broker_name: string | null;
  label: string;
  conditions: Record<string, unknown>;
  commission_percentage: string;
  commission_flat: string;
  premium_base: string;
  max_commission: string | null;
  min_commission: string | null;
  priority: number;
  is_default: boolean;
  is_active: boolean;
  effective_from: string;
  effective_until: string | null;
}

interface CsvPreviewRow {
  row_number?: number;
  is_valid: boolean;
  policy_code?: string;
  policy?: string;
  label?: string;
  commission_percentage?: string | number;
  premium_base?: string;
  errors?: string | string[] | Record<string, string[]>;
}

interface CsvPreview {
  upload_id: string;
  valid_count: number;
  invalid_count: number;
  total_count: number;
  rows: CsvPreviewRow[];
}

interface BrokerAccessEntry {
  id: string;
  name: string;
  code?: string;
  city?: string;
  access_status: string;
}

interface CommissionRateFormData {
  label: string;
  conditions: Record<string, string>;
  commission_percentage: string;
  commission_flat: string;
  premium_base: string;
  is_default: boolean;
  is_active: boolean;
  max_commission: string;
  min_commission: string;
  effective_from: string;
  effective_until: string;
  priority: string;
}

type PolicyPdfField = 'brochure_pdf' | 'policy_wording_pdf' | 'claim_form_pdf' | 'sample_policy_pdf';

// Item 3g: each Key Feature now carries a header AND a description, so the
// broker public site can show it as a feature highlight with a description
// rather than a bare title. `apps.insurers.services.normalize_key_features`
// is the backend's matching shape — a plain string still round-trips (it
// becomes its own header with an empty description) so nothing already saved
// is lost.
interface KeyFeature {
  header: string;
  description: string;
}

interface AddPolicyFormData {
  name: string;
  code: string;
  category_ref: string;
  product: string;
  short_description: string;
  description: string;
  usps: string[];
  key_features: KeyFeature[];
  min_premium: string;
  max_premium: string;
  premium_frequency: string;
  sum_insured_min: string;
  sum_insured_max: string;
  min_age: string;
  max_age: string;
  eligibility_notes: string;
  waiting_period: string;
  // #329 mapping attributes
  uin: string;
  plan_name: string;
  variant_code: string;
  posp_eligible: boolean;
  premium_payment_modes: string[];
  tenure_min_months: string;
  tenure_max_months: string;
  max_renewal_age: string;
  waiting_initial_days: string;
  waiting_specific_months: string;
  waiting_ped_months: string;
  free_look_days: string;
  grace_period_days: string;
  launch_date: string;
  brochure_pdf: File | null;
  policy_wording_pdf: File | null;
  claim_form_pdf: File | null;
  sample_policy_pdf: File | null;
  product_page_url: string;
  claim_process_url: string;
  terms_url: string;
  is_active: boolean;
  is_visible_to_all: boolean;
  tags: string;
}

const initialCreateUserForm: CreateUserFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  password: '',
  role: 'ADMIN',
};

const initialAddPolicyForm: AddPolicyFormData = {
  name: '',
  code: '',
  category_ref: '',
  product: '',
  short_description: '',
  description: '',
  usps: [],
  key_features: [],
  min_premium: '',
  max_premium: '',
  premium_frequency: 'yearly',
  sum_insured_min: '',
  sum_insured_max: '',
  min_age: '',
  max_age: '',
  eligibility_notes: '',
  waiting_period: '',
  uin: '',
  plan_name: '',
  variant_code: '',
  posp_eligible: true,
  premium_payment_modes: ['yearly'],
  tenure_min_months: '',
  tenure_max_months: '',
  max_renewal_age: '',
  waiting_initial_days: '',
  waiting_specific_months: '',
  waiting_ped_months: '',
  free_look_days: '',
  grace_period_days: '',
  launch_date: '',
  brochure_pdf: null,
  policy_wording_pdf: null,
  claim_form_pdf: null,
  sample_policy_pdf: null,
  product_page_url: '',
  claim_process_url: '',
  terms_url: '',
  is_active: true,
  is_visible_to_all: true,
  tags: '',
};

// Mirrors `InsurancePolicy.premium_frequency` choices exactly. It used to
// offer `annual` and `single`, which the model has never accepted — it uses
// `yearly` and `one_time`. That went unnoticed because the field was being
// dropped by the serializer anyway; once it started saving, the form's own
// default would have 400'd every single save.
const PREMIUM_FREQUENCY_OPTIONS = [
  { value: 'one_time', label: 'One Time' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half Yearly' },
  { value: 'yearly', label: 'Yearly' },
];

const INPUT_CLASS =
  'w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

/** One labelled input of the #329 mapping attributes; `inherited` is the catalogue default shown as a hint. */
function MappingField(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'number' | 'date';
  min?: number;
  inherited?: string | number | null;
  placeholder?: string;
}) {
  const { label, value, onChange, type = 'number', min = 0, inherited, placeholder } = props;
  const id = `mapping-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const hint = inherited != null && inherited !== '' ? `Catalogue default: ${inherited}` : placeholder;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <input
        id={id}
        type={type}
        min={type === 'number' ? min : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={hint}
        className={INPUT_CLASS}
      />
    </div>
  );
}

type PolicyFormSection = 'basic' | 'details' | 'premiums' | 'eligibility' | 'documents' | 'settings';

/**
 * Item 3g. Defensive normalization for whatever `key_features` comes back as
 * on a GET: the backend serializer already normalizes every write, but a row
 * saved before migration 0016 ran, or fetched through a path that bypasses
 * the serializer, should still open in the edit form rather than crash it —
 * a plain string becomes its own header with an empty description, matching
 * exactly what the backend's `normalize_key_features` does.
 */
function normalizeKeyFeatures(value: unknown): KeyFeature[] {
  if (!Array.isArray(value)) return [];
  const out: KeyFeature[] = [];
  for (const item of value) {
    if (typeof item === 'string') {
      const header = item.trim();
      if (header) out.push({ header, description: '' });
    } else if (item && typeof item === 'object') {
      const record = item as Record<string, unknown>;
      const header = String(record.header ?? record.title ?? record.label ?? record.name ?? '').trim();
      const description = String(record.description ?? '').trim();
      if (header || description) out.push({ header, description });
    }
  }
  return out;
}

const initialCommissionRateForm: CommissionRateFormData = {
  label: '',
  conditions: {},
  commission_percentage: '',
  commission_flat: '0',
  premium_base: 'net_premium',
  is_default: false,
  is_active: true,
  max_commission: '',
  min_commission: '',
  effective_from: new Date().toISOString().split('T')[0],
  effective_until: '',
  priority: '0',
};

const PREMIUM_BASE_OPTIONS = [
  { value: 'net_premium', label: 'Net Premium' },
  { value: 'gross_premium', label: 'Gross Premium' },
  { value: 'od_premium', label: 'OD Premium' },
  { value: 'tp_premium', label: 'TP Premium' },
  { value: 'base_premium', label: 'Base Premium' },
];

// API Integration types and constants
interface FieldMapping {
  id: string;
  insurer: string;
  insurer_name?: string;
  product_type: string;
  platform_field: string;
  insurer_field: string;
  transformation: 'direct' | 'format' | 'lookup' | 'calculate' | 'custom';
  transformation_display?: string;
  direction: 'request' | 'response' | 'both';
  direction_display?: string;
  is_required: boolean;
  default_value?: string;
  transformation_config?: Record<string, unknown>;
}

interface APIEndpointFormData {
  endpoint_name: string;
  endpoint_url: string;
  http_method: string;
  auth_type: string;
  timeout_seconds: string;
  environment: string;
  is_active: boolean;
}

interface FieldMappingFormData {
  platform_field: string;
  insurer_field: string;
  transformation: string;
  direction: string;
  product_type: string;
  is_required: boolean;
  default_value: string;
}

const initialEndpointForm: APIEndpointFormData = {
  endpoint_name: '',
  endpoint_url: '',
  http_method: 'POST',
  auth_type: 'api_key',
  timeout_seconds: '30',
  environment: 'sandbox',
  is_active: true,
};

const initialMappingForm: FieldMappingFormData = {
  platform_field: '',
  insurer_field: '',
  transformation: 'direct',
  direction: 'both',
  product_type: '',
  is_required: false,
  default_value: '',
};

type IntegrationTab = 'endpoints' | 'mappings' | 'rules' | 'config';

export function InsuranceCompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<CompanySummary | null>(null);
  // Item 3a. Distinct from `!summary`: a failed request must never render as
  // an authoritative "this company does not exist" — that is what made a
  // network blip on this screen look like data loss.
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [brokers, setBrokers] = useState<BrokerRelation[]>([]);
  const [brokersLoading, setBrokersLoading] = useState(false);
  const [brokersError, setBrokersError] = useState<string | null>(null);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [policiesError, setPoliciesError] = useState<string | null>(null);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showAssignBrokerModal, setShowAssignBrokerModal] = useState(false);
  const [showAddPolicyModal, setShowAddPolicyModal] = useState(false);
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);
  const [createUserForm, setCreateUserForm] = useState<CreateUserFormData>(initialCreateUserForm);
  const [addPolicyForm, setAddPolicyForm] = useState<AddPolicyFormData>(initialAddPolicyForm);
  // What a blank #329 field inherits from the mapped catalogue product (shown as placeholders).
  const [catalogueDefaults, setCatalogueDefaults] = useState<Record<string, string | number | null>>({});
  // #283. URLs of the PDFs already on the server for the mapping being edited.
  // A file input has no value to restore, so without this the Documents tab
  // re-opened looking empty and the report read it as "documents were lost".
  const [existingPolicyDocs, setExistingPolicyDocs] = useState<Record<PolicyPdfField, string>>({
    brochure_pdf: '',
    policy_wording_pdf: '',
    claim_form_pdf: '',
    sample_policy_pdf: '',
  });
  // #283. "Remove" on a stored document. Multipart cannot carry a JSON null, so
  // a flagged field is posted as an empty string and the serializer maps it to
  // NULL; without the flag an omitted file simply means "keep what is there".
  const [removePolicyDocs, setRemovePolicyDocs] = useState<Record<PolicyPdfField, boolean>>({
    brochure_pdf: false,
    policy_wording_pdf: false,
    claim_form_pdf: false,
    sample_policy_pdf: false,
  });
  const [masterProducts, setMasterProducts] = useState<{ id: string; name: string; type_name?: string }[]>([]);
  const [newUsp, setNewUsp] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [newFeatureDescription, setNewFeatureDescription] = useState('');
  const [policyFormSection, setPolicyFormSection] = useState<PolicyFormSection>('basic');
  const [submitting, setSubmitting] = useState(false);
  const [commissionPolicy, setCommissionPolicy] = useState<Policy | null>(null);
  const [commissionRates, setCommissionRates] = useState<CommissionRate[]>([]);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [showCommissionForm, setShowCommissionForm] = useState(false);
  const [commissionForm, setCommissionForm] = useState<CommissionRateFormData>(initialCommissionRateForm);
  const [editingRateId, setEditingRateId] = useState<string | null>(null);
  const [conditionKey, setConditionKey] = useState('');
  const [conditionValue, setConditionValue] = useState('');
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<CsvPreview | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvConfirming, setCsvConfirming] = useState(false);
  const [brokerAccessPolicy, setBrokerAccessPolicy] = useState<Policy | null>(null);
  const [brokerAccessList, setBrokerAccessList] = useState<BrokerAccessEntry[]>([]);
  const [brokerAccessLoading, setBrokerAccessLoading] = useState(false);
  const [togglingBrokerId, setTogglingBrokerId] = useState<string | null>(null);

  // API Integration state
  const [integrationTab, setIntegrationTab] = useState<IntegrationTab>('endpoints');
  const [apiConfigs, setApiConfigs] = useState<InsurerAPIConfig[]>([]);
  const [apiConfigsLoading, setApiConfigsLoading] = useState(false);
  const [showEndpointModal, setShowEndpointModal] = useState(false);
  const [endpointForm, setEndpointForm] = useState<APIEndpointFormData>(initialEndpointForm);
  const [editingEndpointId, setEditingEndpointId] = useState<string | null>(null);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [fieldMappingsLoading, setFieldMappingsLoading] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [mappingForm, setMappingForm] = useState<FieldMappingFormData>(initialMappingForm);
  const [editingMappingId, setEditingMappingId] = useState<string | null>(null);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportJson, setBulkImportJson] = useState('');
  const [eligibilityStats, setEligibilityStats] = useState<{ reject: number; allow: number }>({ reject: 0, allow: 0 });
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setSummaryError(null);
    try {
      const response = await api.get(`/insurers/insurers/${id}/summary/`);
      setSummary(response.data);
    } catch (error) {
      // Kept as a distinct error state (not just a toast): a toast disappears,
      // and the page below it must not go on to say "not found" for what was
      // actually a failed request.
      setSummaryError(getErrorMessage(error));
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Item 3a. `summary/` was always the stripped-down action — it never carried
  // legal_name, short_name, website, category, the IRDAI registration number,
  // the country or the logo, even though `InsurerDetailSerializer` (the plain
  // detail endpoint every other insurer screen already uses) has carried all
  // of it since the model was written. This is the fix: fetch that endpoint
  // too, rather than growing `summary/` into a second copy of it.
  const fetchCompany = useCallback(async () => {
    setCompanyError(null);
    try {
      const response = await api.get(`/insurers/insurers/${id}/`);
      setCompany(response.data);
    } catch (error) {
      setCompanyError(getErrorMessage(error));
    }
  }, [id]);

  const fetchPolicies = useCallback(async () => {
    setPoliciesLoading(true);
    setPoliciesError(null);
    try {
      const response = await api.get(`/insurers/country/policies/`, { params: { insurer: id } });
      setPolicies(response.data.results || response.data);
    } catch (error) {
      setPoliciesError(getErrorMessage(error));
      toast.error(getErrorMessage(error));
    } finally {
      setPoliciesLoading(false);
    }
  }, [id]);

  const fetchBrokers = useCallback(async () => {
    setBrokersLoading(true);
    setBrokersError(null);
    try {
      const response = await api.get(`/insurers/insurers/${id}/assigned_brokers/`);
      setBrokers(response.data);
    } catch (error) {
      setBrokersError(getErrorMessage(error));
      toast.error(getErrorMessage(error));
    } finally {
      setBrokersLoading(false);
    }
  }, [id]);

  // Master-data Products feed the "Category" picker in Add Product Mapping (item 2).
  const fetchMasterProducts = async () => {
    try {
      const response = await api.get('/products/products/', { params: { page_size: 200 } });
      const data = response.data.results || response.data;
      setMasterProducts(Array.isArray(data) ? data : []);
    } catch {
      // Products are optional — silently fail
    }
  };

  useEffect(() => {
    if (id) {
      fetchSummary();
      fetchCompany();
      fetchBrokers();
      fetchPolicies();
    }
    fetchMasterProducts();
  }, [id, fetchSummary, fetchCompany, fetchBrokers, fetchPolicies]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createUserForm.email || !createUserForm.password || !createUserForm.first_name) {
      toast.error('First name, email, and password are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/insurers/insurers/${id}/create_user/`, createUserForm);
      toast.success('User created successfully');
      setShowCreateUserModal(false);
      setCreateUserForm(initialCreateUserForm);
      fetchSummary();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddPolicy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addPolicyForm.name || !addPolicyForm.code) {
      toast.error('Name and code are required');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', addPolicyForm.name);
      formData.append('code', addPolicyForm.code);
      formData.append('insurer_id', id || '');
      if (addPolicyForm.product) formData.append('product', addPolicyForm.product);
      if (addPolicyForm.category_ref) formData.append('category_ref', addPolicyForm.category_ref);
      if (addPolicyForm.short_description) formData.append('short_description', addPolicyForm.short_description);
      if (addPolicyForm.description) formData.append('description', addPolicyForm.description);
      // Sent unconditionally, including when empty: gating on `length > 0`
      // meant a USP or feature could be added but never removed — the cleared
      // list simply was not transmitted and the old value survived.
      formData.append('usps', JSON.stringify(addPolicyForm.usps));
      formData.append('key_features', JSON.stringify(addPolicyForm.key_features));
      if (addPolicyForm.min_premium) formData.append('min_premium', addPolicyForm.min_premium);
      if (addPolicyForm.max_premium) formData.append('max_premium', addPolicyForm.max_premium);
      // The list is the source of truth; the server keeps premium_frequency in step (its first mode).
      formData.append('premium_payment_modes', JSON.stringify(addPolicyForm.premium_payment_modes));
      if (addPolicyForm.sum_insured_min) formData.append('sum_insured_min', addPolicyForm.sum_insured_min);
      if (addPolicyForm.sum_insured_max) formData.append('sum_insured_max', addPolicyForm.sum_insured_max);
      if (addPolicyForm.min_age) formData.append('min_age', addPolicyForm.min_age);
      if (addPolicyForm.max_age) formData.append('max_age', addPolicyForm.max_age);
      if (addPolicyForm.eligibility_notes) formData.append('eligibility_notes', addPolicyForm.eligibility_notes);
      if (addPolicyForm.waiting_period) formData.append('waiting_period', addPolicyForm.waiting_period);
      // #329. Strings: '' on edit clears a number/date (the server stores null); the free text fields send as is.
      formData.append('uin', addPolicyForm.uin);
      formData.append('plan_name', addPolicyForm.plan_name);
      formData.append('variant_code', addPolicyForm.variant_code);
      formData.append('posp_eligible', String(addPolicyForm.posp_eligible));
      (
        [
          'tenure_min_months', 'tenure_max_months', 'max_renewal_age', 'waiting_initial_days',
          'waiting_specific_months', 'waiting_ped_months', 'free_look_days', 'grace_period_days', 'launch_date',
        ] as const
      ).forEach((field) => {
        if (addPolicyForm[field] !== '' || editingPolicyId) formData.append(field, addPolicyForm[field]);
      });
      if (addPolicyForm.brochure_pdf) formData.append('brochure_pdf', addPolicyForm.brochure_pdf);
      if (addPolicyForm.policy_wording_pdf) formData.append('policy_wording_pdf', addPolicyForm.policy_wording_pdf);
      if (addPolicyForm.claim_form_pdf) formData.append('claim_form_pdf', addPolicyForm.claim_form_pdf);
      if (addPolicyForm.sample_policy_pdf) formData.append('sample_policy_pdf', addPolicyForm.sample_policy_pdf);
      // A document the user removed and did not replace: send "" so the column
      // is cleared. Choosing a new file wins over the removal flag.
      (['brochure_pdf', 'policy_wording_pdf', 'claim_form_pdf', 'sample_policy_pdf'] as PolicyPdfField[]).forEach((field) => {
        if (removePolicyDocs[field] && !addPolicyForm[field]) formData.append(field, '');
      });
      if (addPolicyForm.product_page_url) formData.append('product_page_url', addPolicyForm.product_page_url);
      if (addPolicyForm.claim_process_url) formData.append('claim_process_url', addPolicyForm.claim_process_url);
      if (addPolicyForm.terms_url) formData.append('terms_url', addPolicyForm.terms_url);
      formData.append('is_active', String(addPolicyForm.is_active));
      formData.append('is_visible_to_all', String(addPolicyForm.is_visible_to_all));
      formData.append(
        'tags',
        JSON.stringify(addPolicyForm.tags.split(',').map((t) => t.trim()).filter(Boolean))
      );

      if (editingPolicyId) {
        // PATCH, not PUT: this form omits empty fields and cannot resend the
        // already-uploaded PDFs (a file input has no value to restore), so a
        // full replace risked clearing whatever was not on screen.
        await api.patch(`/insurers/country/policies/${editingPolicyId}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Product updated successfully');
      } else {
        await api.post('/insurers/country/policies/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Product created successfully');
      }
      setShowAddPolicyModal(false);
      setEditingPolicyId(null);
      setAddPolicyForm(initialAddPolicyForm);
      setExistingPolicyDocs({ brochure_pdf: '', policy_wording_pdf: '', claim_form_pdf: '', sample_policy_pdf: '' });
      setRemovePolicyDocs({ brochure_pdf: false, policy_wording_pdf: false, claim_form_pdf: false, sample_policy_pdf: false });
      setPolicyFormSection('basic');
      fetchPolicies();
      fetchSummary();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const openEditPolicy = async (policy: Policy) => {
    try {
      const response = await api.get(`/insurers/country/policies/${policy.id}/`);
      const p = response.data;
      setAddPolicyForm({
        name: p.name || '',
        code: p.code || '',
        category_ref: p.category_ref || '',
        product: p.product || '',
        short_description: p.short_description || '',
        description: p.description || '',
        usps: p.usps || [],
        key_features: normalizeKeyFeatures(p.key_features),
        min_premium: p.min_premium || '',
        max_premium: p.max_premium || '',
        premium_frequency: p.premium_frequency || '',
        sum_insured_min: p.sum_insured_min || '',
        sum_insured_max: p.sum_insured_max || '',
        min_age: p.min_age ? String(p.min_age) : '',
        max_age: p.max_age ? String(p.max_age) : '',
        eligibility_notes: p.eligibility_notes || '',
        waiting_period: p.waiting_period || '',
        uin: p.uin || '',
        plan_name: p.plan_name || '',
        variant_code: p.variant_code || '',
        posp_eligible: p.posp_eligible ?? true,
        premium_payment_modes: p.premium_payment_modes?.length ? p.premium_payment_modes : p.premium_frequency ? [p.premium_frequency] : [],
        tenure_min_months: p.tenure_min_months != null ? String(p.tenure_min_months) : '',
        tenure_max_months: p.tenure_max_months != null ? String(p.tenure_max_months) : '',
        max_renewal_age: p.max_renewal_age != null ? String(p.max_renewal_age) : '',
        waiting_initial_days: p.waiting_initial_days != null ? String(p.waiting_initial_days) : '',
        waiting_specific_months: p.waiting_specific_months != null ? String(p.waiting_specific_months) : '',
        waiting_ped_months: p.waiting_ped_months != null ? String(p.waiting_ped_months) : '',
        free_look_days: p.free_look_days != null ? String(p.free_look_days) : '',
        grace_period_days: p.grace_period_days != null ? String(p.grace_period_days) : '',
        launch_date: p.launch_date || '',
        brochure_pdf: null,
        policy_wording_pdf: null,
        claim_form_pdf: null,
        sample_policy_pdf: null,
        product_page_url: p.product_page_url || '',
        claim_process_url: p.claim_process_url || '',
        terms_url: p.terms_url || '',
        is_active: p.is_active ?? true,
        is_visible_to_all: p.is_visible_to_all ?? true,
        tags: (p.tags || []).join(', '),
      });
      setExistingPolicyDocs({
        brochure_pdf: p.brochure_pdf || '',
        policy_wording_pdf: p.policy_wording_pdf || '',
        claim_form_pdf: p.claim_form_pdf || '',
        sample_policy_pdf: p.sample_policy_pdf || '',
      });
      setRemovePolicyDocs({ brochure_pdf: false, policy_wording_pdf: false, claim_form_pdf: false, sample_policy_pdf: false });
      setCatalogueDefaults(p.catalogue_defaults || {});
      setEditingPolicyId(policy.id);
      setPolicyFormSection('basic');
      setShowAddPolicyModal(true);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleAssignBrokers = async (brokerIds: string[]) => {
    if (brokerIds.length === 0) return;
    setSubmitting(true);
    try {
      await api.post(`/insurers/insurers/${id}/assign_brokers/`, { broker_ids: brokerIds });
      toast.success('Brokers assigned successfully');
      setShowAssignBrokerModal(false);
      fetchBrokers();
      fetchSummary();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  // Item 3d. "Remove" is a guarded soft delete — the backend refuses with a
  // named reason (409) while the broker still has live API credentials
  // configured for this insurer, rather than silently orphaning them.
  const [brokerActionId, setBrokerActionId] = useState<string | null>(null);

  const handleRemoveBroker = async (broker: BrokerRelation) => {
    if (!window.confirm(`Remove ${broker.name}'s assignment to this insurer? This can be re-assigned later.`)) {
      return;
    }
    setBrokerActionId(broker.id);
    try {
      await api.post(`/insurers/insurers/${id}/unassign_brokers/`, { broker_ids: [broker.id] });
      toast.success(`${broker.name} removed from this insurer`);
      // Verify the write actually landed rather than trusting the 200/409 alone.
      await fetchBrokers();
    } catch (error) {
      // A 409 (still referenced) lands here too, with the backend's own
      // explanation of what still depends on the assignment.
      toast.error(getErrorMessage(error));
    } finally {
      setBrokerActionId(null);
    }
  };

  const handleSuspendBroker = async (broker: BrokerRelation) => {
    setBrokerActionId(broker.id);
    try {
      await api.post(`/insurers/insurers/${id}/suspend_brokers/`, { broker_ids: [broker.id] });
      toast.success(`${broker.name} suspended for this insurer`);
      await fetchBrokers();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBrokerActionId(null);
    }
  };

  // Re-activating a suspended broker reuses `assign_brokers` — it already
  // flips any existing relation back to ACTIVE via `update_or_create`, so a
  // separate "reactivate" endpoint would just be a second way to say the
  // same thing.
  const handleActivateBroker = async (broker: BrokerRelation) => {
    setBrokerActionId(broker.id);
    try {
      await api.post(`/insurers/insurers/${id}/assign_brokers/`, { broker_ids: [broker.id] });
      toast.success(`${broker.name} re-activated for this insurer`);
      await fetchBrokers();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBrokerActionId(null);
    }
  };

  // Item 3c. Removing a created Insurer Admin (or any listed insurer user)
  // revokes real access — see `revoke_insurer_employee_access` on the
  // backend — not just this row. Keyed on `employee_id`, the `InsurerEmployee`
  // row's id, which is what `DELETE /insurers/employees/{id}/` expects; the
  // `InsuranceCompanyUser` row this table is built from has a different id.
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  const handleRemoveInsurerUser = async (user: CompanyUser) => {
    if (!user.employee_id) return;
    if (
      !window.confirm(
        `Remove ${user.user_info.name}'s access to this insurer? This suspends their login.`
      )
    ) {
      return;
    }
    setRemovingUserId(user.id);
    try {
      await api.delete(`/insurers/employees/${user.employee_id}/`);
      toast.success(`${user.user_info.name}'s access was removed`);
      // Verify the write actually landed: re-read the summary rather than
      // trusting the 204 alone.
      await fetchSummary();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setRemovingUserId(null);
    }
  };

  const fetchCommissionRates = async (policyId: string) => {
    setCommissionLoading(true);
    try {
      const response = await api.get('/commissions/policy-rates/', {
        params: { policy: policyId, broker__isnull: true },
      });
      setCommissionRates(response.data.results || response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCommissionLoading(false);
    }
  };

  const openCommissionModal = (policy: Policy) => {
    setCommissionPolicy(policy);
    setShowCommissionForm(false);
    setEditingRateId(null);
    setCommissionForm(initialCommissionRateForm);
    fetchCommissionRates(policy.id);
  };

  const handleSaveCommissionRate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commissionPolicy || !commissionForm.label || !commissionForm.commission_percentage) {
      toast.error('Label and commission percentage are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        policy: commissionPolicy.id,
        broker: null,
        label: commissionForm.label,
        conditions: commissionForm.conditions,
        commission_percentage: commissionForm.commission_percentage,
        commission_flat: commissionForm.commission_flat || '0',
        premium_base: commissionForm.premium_base,
        is_default: commissionForm.is_default,
        is_active: commissionForm.is_active,
        max_commission: commissionForm.max_commission || null,
        min_commission: commissionForm.min_commission || null,
        effective_from: commissionForm.effective_from,
        effective_until: commissionForm.effective_until || null,
        priority: parseInt(commissionForm.priority) || 0,
      };
      if (editingRateId) {
        await api.put(`/commissions/policy-rates/${editingRateId}/`, payload);
        toast.success('Commission rate updated');
      } else {
        await api.post('/commissions/policy-rates/', payload);
        toast.success('Commission rate added');
      }
      setShowCommissionForm(false);
      setEditingRateId(null);
      setCommissionForm(initialCommissionRateForm);
      fetchCommissionRates(commissionPolicy.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditRate = (rate: CommissionRate) => {
    setEditingRateId(rate.id);
    setCommissionForm({
      label: rate.label,
      conditions: rate.conditions as Record<string, string>,
      commission_percentage: rate.commission_percentage,
      commission_flat: rate.commission_flat,
      premium_base: rate.premium_base,
      is_default: rate.is_default,
      is_active: rate.is_active,
      max_commission: rate.max_commission || '',
      min_commission: rate.min_commission || '',
      effective_from: rate.effective_from,
      effective_until: rate.effective_until || '',
      priority: String(rate.priority),
    });
    setShowCommissionForm(true);
  };

  const handleDeleteRate = async (rateId: string) => {
    if (!window.confirm('Delete this commission rate?')) return;
    if (!commissionPolicy) return;
    setSubmitting(true);
    try {
      await api.delete(`/commissions/policy-rates/${rateId}/`);
      toast.success('Commission rate deleted');
      fetchCommissionRates(commissionPolicy.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const addCondition = () => {
    if (!conditionKey.trim()) return;
    setCommissionForm((prev) => ({
      ...prev,
      conditions: { ...prev.conditions, [conditionKey.trim()]: conditionValue.trim() },
    }));
    setConditionKey('');
    setConditionValue('');
  };

  const removeCondition = (key: string) => {
    setCommissionForm((prev) => {
      const next = { ...prev.conditions };
      delete next[key];
      return { ...prev, conditions: next };
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/commissions/policy-rates/download_template/', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'commission_rates_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    setCsvUploading(true);
    setCsvPreview(null);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const response = await api.post('/commissions/policy-rates/upload_csv/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCsvPreview(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCsvUploading(false);
    }
  };

  const handleCsvConfirm = async () => {
    if (!csvPreview?.upload_id) return;
    setCsvConfirming(true);
    try {
      await api.post('/commissions/policy-rates/confirm_upload/', {
        upload_id: csvPreview.upload_id,
      });
      toast.success(`Successfully imported ${csvPreview.valid_count} commission rate(s)`);
      setCsvPreview(null);
      setCsvFile(null);
      setShowCsvUpload(false);
      if (commissionPolicy) {
        fetchCommissionRates(commissionPolicy.id);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCsvConfirming(false);
    }
  };

  const fetchBrokerAccess = async (policyId: string) => {
    setBrokerAccessLoading(true);
    try {
      const response = await api.get(`/insurers/country/policies/${policyId}/broker_access/`);
      setBrokerAccessList(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBrokerAccessLoading(false);
    }
  };

  const openBrokerAccessModal = (policy: Policy) => {
    setBrokerAccessPolicy(policy);
    fetchBrokerAccess(policy.id);
  };

  const handleToggleBrokerAccess = async (brokerId: string, grant: boolean) => {
    if (!brokerAccessPolicy) return;
    setTogglingBrokerId(brokerId);
    try {
      await api.post(`/insurers/country/policies/${brokerAccessPolicy.id}/update_broker_access/`, {
        broker_ids: [brokerId],
        action: grant ? 'grant' : 'revoke',
      });
      toast.success(grant ? 'Access granted' : 'Access revoked');
      fetchBrokerAccess(brokerAccessPolicy.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setTogglingBrokerId(null);
    }
  };

  const handleGrantAll = async () => {
    if (!brokerAccessPolicy) return;
    const noAccessBrokers = brokerAccessList.filter(b => b.access_status === 'NO_ACCESS').map(b => b.id);
    if (noAccessBrokers.length === 0) return;
    setTogglingBrokerId('all');
    try {
      await api.post(`/insurers/country/policies/${brokerAccessPolicy.id}/update_broker_access/`, {
        broker_ids: noAccessBrokers,
        action: 'grant',
      });
      toast.success('All brokers granted access');
      fetchBrokerAccess(brokerAccessPolicy.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setTogglingBrokerId(null);
    }
  };

  // --- API Integration handlers ---
  const fetchApiConfigs = useCallback(async () => {
    if (!id) return;
    setApiConfigsLoading(true);
    try {
      const response = await api.get('/insurers/api-configs/', { params: { insurer: id } });
      setApiConfigs(response.data.results || response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setApiConfigsLoading(false);
    }
  }, [id]);

  const fetchFieldMappings = useCallback(async () => {
    if (!id) return;
    setFieldMappingsLoading(true);
    try {
      const response = await api.get('/insurers/field-mappings/', { params: { insurer: id } });
      setFieldMappings(response.data.results || response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFieldMappingsLoading(false);
    }
  }, [id]);

  const fetchEligibilityStats = useCallback(async () => {
    if (!id) return;
    setEligibilityLoading(true);
    try {
      const response = await api.get('/insurers/eligibility-rules/', { params: { insurer: id, page_size: 1 } });
      const total = response.data.count || 0;
      // Fetch reject count
      const rejectRes = await api.get('/insurers/eligibility-rules/', { params: { insurer: id, rule_type: 'REJECT', page_size: 1 } });
      const rejectCount = rejectRes.data.count || 0;
      setEligibilityStats({ reject: rejectCount, allow: total - rejectCount });
    } catch {
      setEligibilityStats({ reject: 0, allow: 0 });
    } finally {
      setEligibilityLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (integrationTab === 'endpoints') fetchApiConfigs();
    else if (integrationTab === 'mappings') fetchFieldMappings();
    else if (integrationTab === 'rules') fetchEligibilityStats();
  }, [integrationTab, fetchApiConfigs, fetchFieldMappings, fetchEligibilityStats]);

  const handleSaveEndpoint = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!endpointForm.endpoint_name || !endpointForm.endpoint_url) {
      toast.error('Endpoint name and URL are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        insurer: id,
        endpoint_name: endpointForm.endpoint_name,
        endpoint_url: endpointForm.endpoint_url,
        http_method: endpointForm.http_method,
        auth_type: endpointForm.auth_type,
        timeout_seconds: parseInt(endpointForm.timeout_seconds) || 30,
        environment: endpointForm.environment,
        is_active: endpointForm.is_active,
      };
      if (editingEndpointId) {
        await api.patch(`/insurers/api-configs/${editingEndpointId}/`, payload);
        toast.success('Endpoint updated');
      } else {
        await api.post('/insurers/api-configs/', payload);
        toast.success('Endpoint created');
      }
      setShowEndpointModal(false);
      setEditingEndpointId(null);
      setEndpointForm(initialEndpointForm);
      fetchApiConfigs();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const openEditEndpoint = (config: InsurerAPIConfig) => {
    setEndpointForm({
      endpoint_name: config.endpoint_name,
      endpoint_url: config.endpoint_url,
      http_method: config.http_method,
      auth_type: config.auth_type,
      timeout_seconds: String(config.timeout_seconds || 30),
      environment: config.environment,
      is_active: config.is_active,
    });
    setEditingEndpointId(config.id);
    setShowEndpointModal(true);
  };

  const handleDeleteEndpoint = async (configId: string) => {
    if (!window.confirm('Delete this API endpoint?')) return;
    setSubmitting(true);
    try {
      await api.delete(`/insurers/api-configs/${configId}/`);
      toast.success('Endpoint deleted');
      fetchApiConfigs();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveMapping = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mappingForm.platform_field || !mappingForm.insurer_field) {
      toast.error('Platform field and insurer field are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        insurer: id,
        platform_field: mappingForm.platform_field,
        insurer_field: mappingForm.insurer_field,
        transformation: mappingForm.transformation,
        direction: mappingForm.direction,
        product_type: mappingForm.product_type,
        is_required: mappingForm.is_required,
        default_value: mappingForm.default_value,
      };
      if (editingMappingId) {
        await api.patch(`/insurers/field-mappings/${editingMappingId}/`, payload);
        toast.success('Mapping updated');
      } else {
        await api.post('/insurers/field-mappings/', payload);
        toast.success('Mapping created');
      }
      setShowMappingModal(false);
      setEditingMappingId(null);
      setMappingForm(initialMappingForm);
      fetchFieldMappings();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const openEditMapping = (mapping: FieldMapping) => {
    setMappingForm({
      platform_field: mapping.platform_field,
      insurer_field: mapping.insurer_field,
      transformation: mapping.transformation,
      direction: mapping.direction,
      product_type: mapping.product_type || '',
      is_required: mapping.is_required,
      default_value: mapping.default_value || '',
    });
    setEditingMappingId(mapping.id);
    setShowMappingModal(true);
  };

  const handleDeleteMapping = async (mappingId: string) => {
    if (!window.confirm('Delete this field mapping?')) return;
    setSubmitting(true);
    try {
      await api.delete(`/insurers/field-mappings/${mappingId}/`);
      toast.success('Mapping deleted');
      fetchFieldMappings();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkImportMappings = async () => {
    if (!bulkImportJson.trim()) {
      toast.error('Please enter JSON data');
      return;
    }
    setSubmitting(true);
    try {
      const parsed = JSON.parse(bulkImportJson);
      const mappings = Array.isArray(parsed) ? parsed : parsed.mappings || [];
      const enriched = mappings.map((m: Record<string, unknown>) => ({ ...m, insurer: id }));
      for (const mapping of enriched) {
        await api.post('/insurers/field-mappings/', mapping);
      }
      toast.success(`Imported ${enriched.length} mapping(s)`);
      setShowBulkImportModal(false);
      setBulkImportJson('');
      fetchFieldMappings();
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format');
      } else {
        toast.error(getErrorMessage(error));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus('idle');
    try {
      // Check if there are any active endpoints configured
      const activeConfigs = apiConfigs.filter(c => c.is_active);
      if (activeConfigs.length === 0) {
        toast.error('No active API endpoints configured');
        setConnectionStatus('error');
        return;
      }
      // Attempt a lightweight connectivity test
      await api.post(`/insurers/insurers/${id}/test_connectivity/`);
      setConnectionStatus('success');
      toast.success('Connection test successful');
    } catch {
      setConnectionStatus('error');
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Three distinct states: a failed request, an empty result, and "loading"
  // above. A request that failed must never render as an authoritative "this
  // company does not exist" — that used to be the only two states this screen
  // had, so a network blip read exactly like data loss.
  if (summaryError && !summary) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="w-10 h-10 mx-auto text-amber-500" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-3">Could not load this company</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">{summaryError}</p>
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            onClick={() => fetchSummary()}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retry
          </button>
          <button
            onClick={() => navigate('/country/insurance-companies')}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Companies
          </button>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Company not found</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">The insurance company you are looking for does not exist.</p>
        <button
          onClick={() => navigate('/country/insurance-companies')}
          className="mt-4 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors inline-flex items-center gap-2"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Companies
        </button>
      </div>
    );
  }

  const statsCards = [
    {
      title: 'Users',
      value: summary.users_count,
      icon: UsersIcon,
      iconBg: 'bg-blue-100 dark:bg-blue-900/20',
      iconColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      title: 'Products',
      value: summary.policies_count,
      icon: DocumentTextIcon,
      iconBg: 'bg-green-100 dark:bg-green-900/20',
      iconColor: 'text-green-600 dark:text-green-400',
    },
    {
      title: 'Channels',
      value: summary.channels_count,
      icon: ShareIcon,
      iconBg: 'bg-purple-100 dark:bg-purple-900/20',
      iconColor: 'text-purple-600 dark:text-purple-400',
    },
    {
      title: 'Active Brokers',
      value: summary.active_brokers,
      icon: BuildingStorefrontIcon,
      iconBg: 'bg-orange-100 dark:bg-orange-900/20',
      iconColor: 'text-orange-600 dark:text-orange-400',
    },
  ];

  const connectedBrokers = brokers.filter((b) => b.relation_status === 'ACTIVE');
  const unconnectedBrokers = brokers.filter((b) => b.relation_status === 'NOT_CONNECTED');
  // Item 3d: the Mapped Brokers table shows every broker with a live relation
  // — ACTIVE plus SUSPENDED/disabled — so a suspended broker has a row to be
  // re-activated or removed from, instead of disappearing from both this
  // table (ACTIVE only) and the Assign Brokers modal (NOT_CONNECTED only).
  const assignedBrokers = brokers.filter((b) => b.relation_status !== 'NOT_CONNECTED');

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="mb-4">
        <BackButton to="/country/insurance-companies" />
      </div>
      <HospitalNetworkLink insurerId={id} />
      {/* Header — item 3a: the full company record, not just the name. Every
          field here already exists on `InsurerDetailSerializer`; this page
          simply never fetched it before (it only ever called `summary/`). */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          {(company?.logo_display_url || company?.logo_url) ? (
            <img
              src={company.logo_display_url || company.logo_url}
              alt={company.name}
              width={64}
              height={64}
              className="w-16 h-16 rounded-lg object-contain bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 p-1 shrink-0"
            />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center shrink-0">
              <BuildingLibraryIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{summary.name}</h1>
              {company?.code && (
                <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                  {company.code}
                </span>
              )}
              {company?.category_display && (
                <span className="px-2 py-0.5 text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 rounded-full">
                  {company.category_display}
                </span>
              )}
              {company?.country_name && (
                <span className="px-2 py-0.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-full">
                  {company.country_name}
                </span>
              )}
            </div>
            {(company?.legal_name || company?.short_name) && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {company.legal_name}
                {company.legal_name && company.short_name && ' · '}
                {company.short_name && `“${company.short_name}”`}
              </p>
            )}

            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
              {company?.email && (
                <span className="inline-flex items-center gap-1.5">
                  <EnvelopeIcon className="w-4 h-4 text-gray-400" /> {company.email}
                </span>
              )}
              {company?.phone && (
                <span className="inline-flex items-center gap-1.5">
                  <PhoneIcon className="w-4 h-4 text-gray-400" /> {company.phone}
                </span>
              )}
              {company?.website && (
                <a
                  href={company.website}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:underline"
                >
                  <GlobeAltIcon className="w-4 h-4 text-gray-400" /> {company.website}
                </a>
              )}
              {company?.irdai_registration_number && (
                <span>IRDAI Reg. No.: {company.irdai_registration_number}</span>
              )}
              {(company?.network_size || company?.network_label) && (
                <span>
                  {company.network_size != null ? formatNumber(company.network_size) : '—'}
                  {company.network_label ? ` ${company.network_label}` : ' network size'}
                </span>
              )}
            </div>

            {companyError && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                Some company details could not be loaded ({companyError}).{' '}
                <button type="button" onClick={() => fetchCompany()} className="underline">
                  Retry
                </button>
              </p>
            )}

            <p className="text-gray-500 dark:text-gray-400 mt-3 text-sm">
              {summary.total_business.policies_sold > 0 ? (
                <>
                  Total Business:{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {formatCurrency(summary.total_business.premium_collected)}
                  </span>{' '}
                  across {summary.total_business.policies_sold} sold polic
                  {summary.total_business.policies_sold === 1 ? 'y' : 'ies'}
                </>
              ) : (
                'No sold business recorded for this insurer yet.'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat) => (
          <div
            key={stat.title}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{stat.title}</p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {typeof stat.value === 'number' ? formatNumber(stat.value) : stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${stat.iconBg}`}>
                <stat.icon className={`h-6 w-6 ${stat.iconColor}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Users Table. Renamed from "Insurer Employees": item 3b/4 stops new
          Insurer Employees being created (only Insurer Admin creation remains),
          but the table itself still lists whichever pre-existing employee
          logins this insurer has — production has 4 live ones, kept working
          on purpose (they are promoted to insurer_admin in a separate data
          step, not touched here). Calling the table "Admins" would misdescribe
          those rows. */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Insurer Users</h2>
            {/* users_count is the real total; summary.users is capped at 10 by
                the serializer, so counting the array under-reported it. */}
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {summary.users_count} user{summary.users_count === 1 ? '' : 's'}
              {summary.users.length < summary.users_count && ` — showing ${summary.users.length}`}
            </p>
          </div>
          <button
            onClick={() => {
              setCreateUserForm(initialCreateUserForm);
              setShowCreateUserModal(true);
            }}
            className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
          >
            <UserPlusIcon className="w-4 h-4" />
            Create Insurer Admin
          </button>
        </div>
        {summary.users.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {summary.users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
                          <span className="text-xs font-semibold text-primary-600 dark:text-primary-400">
                            {user.user_info.name
                              ? user.user_info.name.charAt(0).toUpperCase()
                              : '?'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {user.user_info.name}
                          </p>
                          {user.user_info.email && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{user.user_info.email}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 capitalize">
                      {user.role}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 text-sm font-medium rounded-full ${
                          user.is_active
                            ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                            : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                        }`}
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {/* Item 3c. B4's per-module rights model does not apply to
                          insurer-side logins at all (`EmployeeRight.broker` is a
                          mandatory FK to a Broker) — there is no rights editor
                          here because there is nothing on this side for one to
                          govern. Remove is the one action that applies. */}
                      <button
                        onClick={() => handleRemoveInsurerUser(user)}
                        disabled={!user.employee_id || removingUserId === user.id}
                        title={user.employee_id ? 'Remove access' : 'No employee record to remove yet'}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <TrashIcon className="w-4 h-4" />
                        {removingUserId === user.id ? 'Removing…' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <UsersIcon className="w-10 h-10 mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No users found for this company.</p>
          </div>
        )}
      </div>

      {/* Mapped Brokers. Item 3d: shows every broker with a live relation
          (ACTIVE or SUSPENDED/disabled), not only ACTIVE ones as before — a
          suspended broker needs a row to be re-activated or removed from,
          or it fell into a gap between "connected" and "assignable". */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Mapped Brokers</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{connectedBrokers.length} brokers connected</p>
          </div>
          <button
            onClick={() => setShowAssignBrokerModal(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Assign Broker
          </button>
        </div>
        {brokersLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : brokersError ? (
          <div className="text-center py-8">
            <ExclamationTriangleIcon className="w-10 h-10 mx-auto text-amber-500" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Could not load mapped brokers: {brokersError}</p>
            <button
              onClick={() => fetchBrokers()}
              className="mt-3 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : assignedBrokers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Broker
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {assignedBrokers.map((broker) => {
                  const isActive = broker.relation_status === 'ACTIVE';
                  const isBusy = brokerActionId === broker.id;
                  return (
                    <tr key={broker.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center shrink-0">
                            <BuildingStorefrontIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                          </div>
                          {/* Item 3f: EntityLink already gates the link by the
                              viewer's role, so it never points somewhere the
                              route would refuse — no hand-rolled <Link>. */}
                          <EntityLink
                            kind="broker"
                            id={broker.id}
                            name={broker.name}
                            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{broker.code}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                            isActive ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                          }`}
                        >
                          {isActive ? 'Active' : broker.relation_status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isActive ? (
                            <button
                              onClick={() => handleSuspendBroker(broker)}
                              disabled={isBusy}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <PauseIcon className="w-4 h-4" />
                              Suspend
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivateBroker(broker)}
                              disabled={isBusy}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <PlayIcon className="w-4 h-4" />
                              Activate
                            </button>
                          )}
                          <button
                            onClick={() => handleRemoveBroker(broker)}
                            disabled={isBusy}
                            title="Remove this broker's assignment (refused while live API credentials depend on it)"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <TrashIcon className="w-4 h-4" />
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <BuildingStorefrontIcon className="w-10 h-10 mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No brokers mapped to this company yet.</p>
          </div>
        )}
      </div>

      {/* Products Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Products</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{policies.length} products</p>
          </div>
          <button
            onClick={() => {
              setAddPolicyForm(initialAddPolicyForm);
              setCatalogueDefaults({});
              setExistingPolicyDocs({ brochure_pdf: '', policy_wording_pdf: '', claim_form_pdf: '', sample_policy_pdf: '' });
              setRemovePolicyDocs({ brochure_pdf: false, policy_wording_pdf: false, claim_form_pdf: false, sample_policy_pdf: false });
              setEditingPolicyId(null);
              setShowAddPolicyModal(true);
            }}
            className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Add Product Mapping
          </button>
        </div>
        {policiesLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : policiesError ? (
          <div className="text-center py-8">
            <ExclamationTriangleIcon className="w-10 h-10 mx-auto text-amber-500" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Could not load products: {policiesError}</p>
            <button
              onClick={() => fetchPolicies()}
              className="mt-3 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : policies.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Visibility
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {/* COUNTRY-3: a saved product opens on click, not only via the
                    small pencil. There is no separate read-only details route
                    for a mapping, and this modal now genuinely shows the
                    product's details — all six tabs finally load and save — so
                    it IS the details view. The action buttons below stop
                    propagation so they still do their own thing. */}
                {policies.map((policy) => (
                  <tr
                    key={policy.id}
                    onClick={() => openEditPolicy(policy)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{policy.name}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{policy.code}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 capitalize">
                      {policy.category_name || policy.category}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                        {policy.short_description || '-'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                          policy.is_active
                            ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {policy.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                          policy.is_visible_to_all
                            ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {policy.is_visible_to_all ? 'All Brokers' : 'Restricted'}
                      </span>
                    </td>
                    {/* Without this, clicking Brokers or Commission would ALSO
                        fire the row handler and open the edit modal on top of
                        the one just requested. */}
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditPolicy(policy)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/20 transition-colors"
                        >
                          <PencilIcon className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => openBrokerAccessModal(policy)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/20 transition-colors"
                        >
                          <UsersIcon className="w-4 h-4" />
                          Brokers
                        </button>
                        <button
                          onClick={() => openCommissionModal(policy)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/20 transition-colors"
                        >
                          <CurrencyRupeeIcon className="w-4 h-4" />
                          Commission
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <DocumentTextIcon className="w-10 h-10 mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No product mappings found. Click &quot;Add Product Mapping&quot; to create one.</p>
          </div>
        )}
      </div>

      {/* API Integration Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Cog6ToothIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">API Integration</h2>
          </div>
        </div>

        {/* Sub-tab navigation */}
        <div className="px-6 pt-4 flex gap-2">
          {([
            { key: 'endpoints' as IntegrationTab, label: 'Endpoints' },
            { key: 'mappings' as IntegrationTab, label: 'Field Mappings' },
            { key: 'rules' as IntegrationTab, label: 'Rules' },
            { key: 'config' as IntegrationTab, label: 'Config' },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setIntegrationTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                integrationTab === tab.key
                  ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-800'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Sub-tab content */}
        <div className="p-6">
          {/* Endpoints Tab */}
          {integrationTab === 'endpoints' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">{apiConfigs.length} endpoint(s) configured</p>
                <button
                  onClick={() => {
                    setEndpointForm(initialEndpointForm);
                    setEditingEndpointId(null);
                    setShowEndpointModal(true);
                  }}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Endpoint
                </button>
              </div>
              {apiConfigsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                </div>
              ) : apiConfigs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Endpoint Name</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">URL Path</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Method</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Auth Type</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Timeout</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Environment</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Active</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {apiConfigs.map((config) => (
                        <tr key={config.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{config.endpoint_name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 font-mono truncate max-w-xs">{config.endpoint_url}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                              config.http_method === 'GET' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' : 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                            }`}>
                              {config.http_method}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{config.auth_type_display || config.auth_type}</td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{config.timeout_seconds || 30}s</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                              config.environment === 'production' ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400' : 'bg-yellow-100 dark:bg-amber-900/20 text-yellow-700 dark:text-amber-400'
                            }`}>
                              {config.environment_display || config.environment}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                              config.is_active ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}>
                              {config.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => openEditEndpoint(config)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/20 transition-colors"
                              >
                                <PencilIcon className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteEndpoint(config.id)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Cog6ToothIcon className="w-10 h-10 mx-auto text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No API endpoints configured. Click &quot;Add Endpoint&quot; to create one.</p>
                </div>
              )}
            </div>
          )}

          {/* Field Mappings Tab */}
          {integrationTab === 'mappings' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">{fieldMappings.length} mapping(s) configured</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowBulkImportModal(true)}
                    className="text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
                  >
                    <ArrowUpTrayIcon className="w-4 h-4" />
                    Bulk Import
                  </button>
                  <button
                    onClick={() => {
                      setMappingForm(initialMappingForm);
                      setEditingMappingId(null);
                      setShowMappingModal(true);
                    }}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Mapping
                  </button>
                </div>
              </div>
              {fieldMappingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                </div>
              ) : fieldMappings.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Platform Field</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Insurer Field</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Transformation</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Direction</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Product Type</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {fieldMappings.map((mapping) => (
                        <tr key={mapping.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100 font-mono">{mapping.platform_field}</td>
                          <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 font-mono">{mapping.insurer_field}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                              mapping.transformation === 'direct' ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' :
                              mapping.transformation === 'format' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' :
                              mapping.transformation === 'lookup' ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400' :
                              mapping.transformation === 'calculate' ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400' :
                              'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                            }`}>
                              {mapping.transformation_display || mapping.transformation}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{mapping.direction_display || mapping.direction}</td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{mapping.product_type || '-'}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => openEditMapping(mapping)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/20 transition-colors"
                              >
                                <PencilIcon className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteMapping(mapping.id)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <DocumentTextIcon className="w-10 h-10 mx-auto text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No field mappings configured. Click &quot;Add Mapping&quot; to create one.</p>
                </div>
              )}
            </div>
          )}

          {/* Rules Tab */}
          {integrationTab === 'rules' && (
            <div>
              {eligibilityLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <p className="text-sm font-medium text-red-700 dark:text-red-400">REJECT Rules</p>
                      <p className="text-2xl font-bold text-red-900 dark:text-red-200 mt-1">{eligibilityStats.reject}</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                      <p className="text-sm font-medium text-green-700 dark:text-green-400">ALLOW Rules</p>
                      <p className="text-2xl font-bold text-green-900 dark:text-green-200 mt-1">{eligibilityStats.allow}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => navigate(`/admin/eligibility-rules?insurer=${id}`)}
                      className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 transition-colors"
                    >
                      <Cog6ToothIcon className="w-4 h-4" />
                      Manage Rules
                    </button>
                    <button
                      onClick={fetchEligibilityStats}
                      className="text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 transition-colors"
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                      Refresh
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Config Tab */}
          {integrationTab === 'config' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Sandbox Status */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Sandbox</p>
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                      apiConfigs.some(c => c.environment === 'sandbox' && c.is_active)
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                      {apiConfigs.some(c => c.environment === 'sandbox' && c.is_active) ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {apiConfigs.filter(c => c.environment === 'sandbox').length} endpoint(s) configured,{' '}
                    {apiConfigs.filter(c => c.environment === 'sandbox' && c.is_active).length} active
                  </p>
                </div>

                {/* Production Status */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Production</p>
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                      apiConfigs.some(c => c.environment === 'production' && c.is_active)
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                      {apiConfigs.some(c => c.environment === 'production' && c.is_active) ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {apiConfigs.filter(c => c.environment === 'production').length} endpoint(s) configured,{' '}
                    {apiConfigs.filter(c => c.environment === 'production' && c.is_active).length} active
                  </p>
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Test Connection</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Verify connectivity to the insurer API</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {connectionStatus === 'success' && (
                      <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                        <CheckCircleIcon className="w-5 h-5" />
                        Connected
                      </span>
                    )}
                    {connectionStatus === 'error' && (
                      <span className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                        <XCircleIcon className="w-5 h-5" />
                        Failed
                      </span>
                    )}
                    <button
                      onClick={handleTestConnection}
                      disabled={testingConnection}
                      className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {testingConnection ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      ) : (
                        <SignalIcon className="w-4 h-4" />
                      )}
                      Test Connection
                    </button>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Integration Summary</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{apiConfigs.length}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Endpoints</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{fieldMappings.length}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Field Mappings</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{eligibilityStats.reject + eligibilityStats.allow}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Eligibility Rules</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{apiConfigs.filter(c => c.is_active).length}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Active Endpoints</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Endpoint Modal */}
      {showEndpointModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowEndpointModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editingEndpointId ? 'Edit Endpoint' : 'Add Endpoint'}
                </h3>
                <button
                  onClick={() => setShowEndpointModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleSaveEndpoint}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Endpoint Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={endpointForm.endpoint_name}
                      onChange={(e) => setEndpointForm({ ...endpointForm, endpoint_name: e.target.value })}
                      placeholder="e.g., get_quote, issue_policy"
                      required
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      URL Path <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={endpointForm.endpoint_url}
                      onChange={(e) => setEndpointForm({ ...endpointForm, endpoint_url: e.target.value })}
                      placeholder="https://api.insurer.com/v1/quotes"
                      required
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <SearchableSelect
                      label="HTTP Method"
                      value={endpointForm.http_method}
                      onChange={(value) => setEndpointForm({ ...endpointForm, http_method: value })}
                      options={[
                        { value: 'GET', label: 'GET' },
                        { value: 'POST', label: 'POST' },
                        { value: 'PUT', label: 'PUT' },
                        { value: 'PATCH', label: 'PATCH' },
                      ]}
                    />
                    <SearchableSelect
                      label="Auth Type"
                      value={endpointForm.auth_type}
                      onChange={(value) => setEndpointForm({ ...endpointForm, auth_type: value })}
                      options={[
                        { value: 'none', label: 'None' },
                        { value: 'api_key', label: 'API Key' },
                        { value: 'basic', label: 'Basic Auth' },
                        { value: 'oauth2', label: 'OAuth 2.0' },
                        { value: 'certificate', label: 'Client Certificate' },
                      ]}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Timeout (seconds)</label>
                      <input
                        type="number"
                        value={endpointForm.timeout_seconds}
                        onChange={(e) => setEndpointForm({ ...endpointForm, timeout_seconds: e.target.value })}
                        min="1"
                        max="300"
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <SearchableSelect
                      label="Environment"
                      value={endpointForm.environment}
                      onChange={(value) => setEndpointForm({ ...endpointForm, environment: value })}
                      options={[
                        { value: 'sandbox', label: 'Sandbox' },
                        { value: 'production', label: 'Production' },
                      ]}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="endpoint-active"
                      checked={endpointForm.is_active}
                      onChange={(e) => setEndpointForm({ ...endpointForm, is_active: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="endpoint-active" className="text-sm text-gray-700 dark:text-gray-300">Active</label>
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowEndpointModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                    {editingEndpointId ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Field Mapping Modal */}
      {showMappingModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowMappingModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editingMappingId ? 'Edit Field Mapping' : 'Add Field Mapping'}
                </h3>
                <button
                  onClick={() => setShowMappingModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleSaveMapping}>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Platform Field <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={mappingForm.platform_field}
                        onChange={(e) => setMappingForm({ ...mappingForm, platform_field: e.target.value })}
                        placeholder="e.g., customer_name"
                        required
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Insurer Field <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={mappingForm.insurer_field}
                        onChange={(e) => setMappingForm({ ...mappingForm, insurer_field: e.target.value })}
                        placeholder="e.g., proposer_name"
                        required
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <SearchableSelect
                      label="Transformation"
                      value={mappingForm.transformation}
                      onChange={(value) => setMappingForm({ ...mappingForm, transformation: value })}
                      options={[
                        { value: 'direct', label: 'Direct Copy' },
                        { value: 'format', label: 'Format Change' },
                        { value: 'lookup', label: 'Value Lookup' },
                        { value: 'calculate', label: 'Calculate' },
                        { value: 'custom', label: 'Custom Function' },
                      ]}
                    />
                    <SearchableSelect
                      label="Direction"
                      value={mappingForm.direction}
                      onChange={(value) => setMappingForm({ ...mappingForm, direction: value })}
                      options={[
                        { value: 'both', label: 'Both' },
                        { value: 'request', label: 'Request' },
                        { value: 'response', label: 'Response' },
                      ]}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Product Type</label>
                    <input
                      type="text"
                      value={mappingForm.product_type}
                      onChange={(e) => setMappingForm({ ...mappingForm, product_type: e.target.value })}
                      placeholder="e.g., motor, health (leave empty for all)"
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Value</label>
                    <input
                      type="text"
                      value={mappingForm.default_value}
                      onChange={(e) => setMappingForm({ ...mappingForm, default_value: e.target.value })}
                      placeholder="Default value if source is empty"
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="mapping-required"
                      checked={mappingForm.is_required}
                      onChange={(e) => setMappingForm({ ...mappingForm, is_required: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="mapping-required" className="text-sm text-gray-700 dark:text-gray-300">Required field</label>
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowMappingModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                    {editingMappingId ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowBulkImportModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Bulk Import Field Mappings</h3>
                <button
                  onClick={() => setShowBulkImportModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Paste a JSON array of field mappings. Each object should have: platform_field, insurer_field, transformation, direction, product_type.
                </p>
                <textarea
                  value={bulkImportJson}
                  onChange={(e) => setBulkImportJson(e.target.value)}
                  rows={10}
                  placeholder={`[\n  {\n    "platform_field": "customer_name",\n    "insurer_field": "proposer_name",\n    "transformation": "direct",\n    "direction": "request"\n  }\n]`}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowBulkImportModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkImportMappings}
                  disabled={submitting || !bulkImportJson.trim()}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                  Import
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Insurer Admin Modal. Item 3b/4: the role picker below used to
          offer Admin/Manager/Staff — Manager and Staff both mapped onto the
          insurer_employee login (see InsurerUserCreateSerializer.USER_ROLE_BY_ROLE
          on the backend), and only Insurer Admins may be created from here now.
          Production's existing insurer_employee logins are untouched by this —
          they are promoted to insurer_admin in a separate data step, not here —
          this only stops the UI from creating new ones. `createUserForm.role`
          stays "ADMIN" (its only value now; see `initialCreateUserForm`). */}
      {showCreateUserModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowCreateUserModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create Insurer Admin</h3>
                <button
                  onClick={() => setShowCreateUserModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleCreateUser}>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={createUserForm.first_name}
                        onChange={(e) => setCreateUserForm({ ...createUserForm, first_name: e.target.value })}
                        placeholder="First name"
                        required
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={createUserForm.last_name}
                        onChange={(e) => setCreateUserForm({ ...createUserForm, last_name: e.target.value })}
                        placeholder="Last name"
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={createUserForm.email}
                        onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                        placeholder="user@company.com"
                        required
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={createUserForm.phone}
                        onChange={(e) => setCreateUserForm({ ...createUserForm, phone: e.target.value })}
                        placeholder="+91 XXXXXXXXXX"
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={createUserForm.password}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                      placeholder="Set a password"
                      required
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateUserModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {submitting && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    )}
                    Create Insurer Admin
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Assign Broker Modal */}
      {showAssignBrokerModal && (
        <AssignBrokerModal
          brokers={unconnectedBrokers}
          onAssign={handleAssignBrokers}
          onClose={() => setShowAssignBrokerModal(false)}
          submitting={submitting}
        />
      )}

      {/* Commission Rate Card Modal */}
      {commissionPolicy && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setCommissionPolicy(null)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {commissionPolicy.name} - Base Commission Rates
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    Manage insurer base commission rates for this policy
                  </p>
                </div>
                <button
                  onClick={() => setCommissionPolicy(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Board #199 — these rates used to be written here and read by
                  nothing at all. The commission engine now resolves them, one
                  tier BELOW a broker's own negotiated grid, so they apply where
                  that broker has no rate of their own. Saying so here is the
                  point: a rate whose scope nobody can state is a payout nobody
                  can predict. */}
              <div className="mb-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-xs text-blue-800 dark:text-blue-300">
                <p className="font-medium">How these rates are applied</p>
                <p className="mt-1">
                  They cover every policy sold on this product offering, for any broker that has no
                  rate of its own with this insurer. A broker&apos;s own rate always wins. A rate that
                  carries Conditions is <strong>not</strong> applied — see below.
                </p>
              </div>

              {/* Existing Rates Table */}
              {commissionLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                </div>
              ) : commissionRates.length > 0 ? (
                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Label</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Conditions</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Rate %</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Premium Base</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Default</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Active</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {commissionRates.map((rate) => (
                        <tr key={rate.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-2 font-medium text-gray-900 dark:text-gray-100">{rate.label}</td>
                          <td className="px-4 py-2">
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(rate.conditions || {}).map(([k, v]) => (
                                <span key={k} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                  {k}: {String(v)}
                                </span>
                              ))}
                              {Object.keys(rate.conditions || {}).length === 0 ? (
                                <span className="text-xs text-gray-400">None</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
                                  Not applied
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2">{rate.commission_percentage}%</td>
                          <td className="px-4 py-2 capitalize">{rate.premium_base.replace('_', ' ')}</td>
                          <td className="px-4 py-2">
                            {rate.is_default ? (
                              <span className="text-xs font-medium text-green-700 dark:text-green-400">Yes</span>
                            ) : (
                              <span className="text-xs text-gray-400">No</span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${rate.is_active ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                              {rate.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleEditRate(rate)}
                                className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                                title="Edit"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteRate(rate.id)}
                                className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6 mb-4">
                  <CurrencyRupeeIcon className="w-8 h-8 mx-auto text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No commission rates configured yet.</p>
                </div>
              )}

              {/* Add/Edit Form */}
              {showCommissionForm ? (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    {editingRateId ? 'Edit Commission Rate' : 'Add Commission Rate'}
                  </h4>
                  <form onSubmit={handleSaveCommissionRate}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Label <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={commissionForm.label}
                          onChange={(e) => setCommissionForm({ ...commissionForm, label: e.target.value })}
                          placeholder="e.g. Private Car <1500cc in MH"
                          required
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Commission % <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={commissionForm.commission_percentage}
                          onChange={(e) => setCommissionForm({ ...commissionForm, commission_percentage: e.target.value })}
                          required
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Commission Flat</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={commissionForm.commission_flat}
                          onChange={(e) => setCommissionForm({ ...commissionForm, commission_flat: e.target.value })}
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <SearchableSelect
                        label="Premium Base"
                        value={commissionForm.premium_base}
                        onChange={(value) => setCommissionForm({ ...commissionForm, premium_base: value })}
                        options={PREMIUM_BASE_OPTIONS}
                      />
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                        <input
                          type="number"
                          value={commissionForm.priority}
                          onChange={(e) => setCommissionForm({ ...commissionForm, priority: e.target.value })}
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Effective From</label>
                        <input
                          type="date"
                          value={commissionForm.effective_from}
                          onChange={(e) => setCommissionForm({ ...commissionForm, effective_from: e.target.value })}
                          required
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Effective Until</label>
                        <input
                          type="date"
                          value={commissionForm.effective_until}
                          onChange={(e) => setCommissionForm({ ...commissionForm, effective_until: e.target.value })}
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      {/* Conditions builder */}
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Conditions</label>
                        <p className="mb-2 text-xs text-amber-700 dark:text-amber-400">
                          Free-text notes only. The commission engine cannot evaluate them, and it
                          SKIPS any rate that carries one rather than applying it to policies the
                          condition was meant to exclude. Leave this empty for the rate to pay. To
                          narrow a rate by CC, GVW, age, seating, NCB, vehicle condition or location,
                          use the broker-side Add Rate form, which has typed fields the engine matches.
                        </p>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {Object.entries(commissionForm.conditions).map(([k, v]) => (
                            <span key={k} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-xs">
                              {k}: {v}
                              <button type="button" onClick={() => removeCondition(k)} className="hover:text-red-600 dark:hover:text-red-400">
                                <XMarkIcon className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Key (e.g. state)"
                            value={conditionKey}
                            onChange={(e) => setConditionKey(e.target.value)}
                            className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                          <input
                            type="text"
                            placeholder="Value (e.g. MH)"
                            value={conditionValue}
                            onChange={(e) => setConditionValue(e.target.value)}
                            className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                          <button
                            type="button"
                            onClick={addCondition}
                            className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20"
                          >
                            Add
                          </button>
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={commissionForm.is_default}
                            onChange={(e) => setCommissionForm({ ...commissionForm, is_default: e.target.checked })}
                            className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Default rate (fallback when no conditions match)</span>
                        </label>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Max Commission</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={commissionForm.max_commission}
                          onChange={(e) => setCommissionForm({ ...commissionForm, max_commission: e.target.value })}
                          placeholder="Optional"
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Min Commission</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={commissionForm.min_commission}
                          onChange={(e) => setCommissionForm({ ...commissionForm, min_commission: e.target.value })}
                          placeholder="Optional"
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={commissionForm.is_active}
                            onChange={(e) => setCommissionForm({ ...commissionForm, is_active: e.target.checked })}
                            className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                        </label>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCommissionForm(false);
                          setEditingRateId(null);
                          setCommissionForm(initialCommissionRateForm);
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                      >
                        {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                        {editingRateId ? 'Update Rate' : 'Add Rate'}
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setEditingRateId(null);
                      setCommissionForm(initialCommissionRateForm);
                      setShowCommissionForm(true);
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Rate
                  </button>
                  <button
                    onClick={() => {
                      setShowCsvUpload(!showCsvUpload);
                      setCsvPreview(null);
                      setCsvFile(null);
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                  >
                    <ArrowUpTrayIcon className="w-4 h-4" />
                    Bulk Upload
                  </button>
                </div>
              )}

              {/* CSV Bulk Upload Section */}
              {showCsvUpload && !showCommissionForm && (
                <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Bulk Upload Commission Rates</h4>
                    <button
                      onClick={() => {
                        setShowCsvUpload(false);
                        setCsvPreview(null);
                        setCsvFile(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mb-3">
                    <button
                      type="button"
                      onClick={handleDownloadTemplate}
                      className="text-sm text-primary-600 dark:text-primary-400 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline"
                    >
                      Download CSV Template
                    </button>
                  </div>

                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => {
                        setCsvFile(e.target.files?.[0] || null);
                        setCsvPreview(null);
                      }}
                      className="block text-sm text-gray-500 dark:text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-300 dark:file:border-gray-600 file:text-sm file:font-medium file:bg-white dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-300 hover:file:bg-gray-50 dark:hover:file:bg-gray-600"
                    />
                    <button
                      type="button"
                      onClick={handleCsvUpload}
                      disabled={!csvFile || csvUploading}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {csvUploading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      )}
                      Upload &amp; Preview
                    </button>
                  </div>

                  {/* Preview Results */}
                  {csvPreview && (
                    <div className="mt-4">
                      <div className="flex items-center gap-4 mb-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400">
                          Valid: {csvPreview.valid_count}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400">
                          Invalid: {csvPreview.invalid_count}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Total rows: {csvPreview.total_count}
                        </span>
                      </div>

                      {csvPreview.rows && csvPreview.rows.length > 0 && (
                        <div className="overflow-x-auto max-h-72 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                            <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Row</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Status</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Product</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Label</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Rate %</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Premium Base</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Errors</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                              {csvPreview.rows.slice(0, 20).map((row: CsvPreviewRow, idx: number) => (
                                <tr
                                  key={idx}
                                  className={row.is_valid ? 'bg-white dark:bg-gray-800' : 'bg-red-50 dark:bg-red-900/20'}
                                >
                                  <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{row.row_number ?? idx + 1}</td>
                                  <td className="px-3 py-1.5">
                                    {row.is_valid ? (
                                      <span className="text-green-700 dark:text-green-400 font-medium">Valid</span>
                                    ) : (
                                      <span className="text-red-700 dark:text-red-400 font-medium">Invalid</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{row.policy_code || row.policy || '-'}</td>
                                  <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{row.label || '-'}</td>
                                  <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{row.commission_percentage ?? '-'}</td>
                                  <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{row.premium_base || '-'}</td>
                                  <td className="px-3 py-1.5 text-red-600 dark:text-red-400">
                                    {row.errors
                                      ? typeof row.errors === 'string'
                                        ? row.errors
                                        : Array.isArray(row.errors)
                                        ? row.errors.join('; ')
                                        : Object.values(row.errors).flat().join('; ')
                                      : ''}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {csvPreview.rows && csvPreview.rows.length > 20 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Showing first 20 of {csvPreview.rows.length} rows.
                        </p>
                      )}

                      <div className="mt-4 flex items-center gap-3">
                        {csvPreview.valid_count > 0 && (
                          <button
                            type="button"
                            onClick={handleCsvConfirm}
                            disabled={csvConfirming}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {csvConfirming && (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            )}
                            Confirm Import ({csvPreview.valid_count} rows)
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setCsvPreview(null);
                            setCsvFile(null);
                            setShowCsvUpload(false);
                          }}
                          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setCommissionPolicy(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddPolicyModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => { setShowAddPolicyModal(false); setEditingPolicyId(null); }} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{editingPolicyId ? 'Edit Product Mapping' : 'Add Product Mapping'}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{editingPolicyId ? 'Update the insurance policy details' : 'Create a new insurance policy with detailed information'}</p>
                </div>
                <button
                  onClick={() => { setShowAddPolicyModal(false); setEditingPolicyId(null); setPolicyFormSection('basic'); }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Section Tabs */}
              <div className="flex flex-wrap gap-1 mb-6 border-b border-gray-200 dark:border-gray-700 pb-3">
                {([
                  { key: 'basic' as const, label: 'Basic Info' },
                  { key: 'details' as const, label: 'USPs & Features' },
                  { key: 'premiums' as const, label: 'Premiums' },
                  { key: 'eligibility' as const, label: 'Eligibility' },
                  { key: 'documents' as const, label: 'Documents & Links' },
                  { key: 'settings' as const, label: 'Settings' },
                ]).map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setPolicyFormSection(tab.key)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      policyFormSection === tab.key
                        ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <form onSubmit={handleAddPolicy}>
                {/* Basic Info Section */}
                {policyFormSection === 'basic' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={addPolicyForm.name}
                          onChange={(e) => setAddPolicyForm({ ...addPolicyForm, name: e.target.value })}
                          placeholder="Product name"
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={addPolicyForm.code}
                          onChange={(e) => setAddPolicyForm({ ...addPolicyForm, code: e.target.value })}
                          placeholder="POL-001"
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <MappingField label="IRDAI UIN" type="text" value={addPolicyForm.uin} placeholder="e.g. ACMHLIP26001V012526"
                        inherited={catalogueDefaults.uin}
                        onChange={(value) => setAddPolicyForm({ ...addPolicyForm, uin: value.toUpperCase() })} />
                      <MappingField label="Plan" type="text" value={addPolicyForm.plan_name} placeholder="e.g. Optima Secure"
                        onChange={(value) => setAddPolicyForm({ ...addPolicyForm, plan_name: value })} />
                      <MappingField label="Variant" type="text" value={addPolicyForm.variant_code} placeholder="e.g. Plus"
                        onChange={(value) => setAddPolicyForm({ ...addPolicyForm, variant_code: value })} />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={addPolicyForm.posp_eligible}
                        onChange={(e) => setAddPolicyForm({ ...addPolicyForm, posp_eligible: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Available to POSPs</span>
                    </label>
                    <SearchableSelect
                      label="Category"
                      value={addPolicyForm.product}
                      onChange={(value) => setAddPolicyForm({ ...addPolicyForm, product: value })}
                      placeholder="-- Select Product --"
                      helperText="Loaded from Master Data > Products"
                      options={masterProducts.map((prod) => ({
                        value: prod.id,
                        label: prod.type_name ? `${prod.type_name} — ${prod.name}` : prod.name,
                      }))}
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Short Description
                      </label>
                      <input
                        type="text"
                        value={addPolicyForm.short_description}
                        onChange={(e) => setAddPolicyForm({ ...addPolicyForm, short_description: e.target.value })}
                        placeholder="One-line summary of the product"
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Full Description
                      </label>
                      <textarea
                        value={addPolicyForm.description}
                        onChange={(e) => setAddPolicyForm({ ...addPolicyForm, description: e.target.value })}
                        placeholder="Detailed description of the product"
                        rows={4}
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                )}

                {/* USPs & Key Features Section */}
                {policyFormSection === 'details' && (
                  <div className="space-y-6">
                    {/* USPs */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Unique Selling Points (USPs)
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {addPolicyForm.usps.map((usp, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-xs font-medium">
                            {usp}
                            <button
                              type="button"
                              onClick={() => setAddPolicyForm({ ...addPolicyForm, usps: addPolicyForm.usps.filter((_, i) => i !== idx) })}
                              className="hover:text-red-600 dark:hover:text-red-400"
                            >
                              <XMarkIcon className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newUsp}
                          onChange={(e) => setNewUsp(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (newUsp.trim()) {
                                setAddPolicyForm({ ...addPolicyForm, usps: [...addPolicyForm.usps, newUsp.trim()] });
                                setNewUsp('');
                              }
                            }
                          }}
                          placeholder="e.g. No medical check-up required"
                          className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (newUsp.trim()) {
                              setAddPolicyForm({ ...addPolicyForm, usps: [...addPolicyForm.usps, newUsp.trim()] });
                              setNewUsp('');
                            }
                          }}
                          className="px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    </div>

                    {/* Key Features — item 3g: a header AND a description each,
                        so the broker public site can show every feature as a
                        highlight with a description, not just a bare title. */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Key Features
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Each feature needs a short header; the description is optional and shows
                        underneath it on the broker&apos;s public site.
                      </p>
                      <div className="space-y-2 mb-3">
                        {addPolicyForm.key_features.map((feat, idx) => (
                          <div
                            key={idx}
                            className="flex items-start justify-between gap-2 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-green-900 dark:text-green-200">{feat.header}</p>
                              {feat.description && (
                                <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">{feat.description}</p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setAddPolicyForm({
                                  ...addPolicyForm,
                                  key_features: addPolicyForm.key_features.filter((_, i) => i !== idx),
                                })
                              }
                              className="text-green-700 dark:text-green-400 hover:text-red-600 dark:hover:text-red-400 shrink-0"
                              aria-label={`Remove feature ${feat.header}`}
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        {addPolicyForm.key_features.length === 0 && (
                          <p className="text-xs text-gray-400 italic">No key features added yet.</p>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={newFeature}
                          onChange={(e) => setNewFeature(e.target.value)}
                          placeholder="Header, e.g. Cashless hospitalization"
                          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <input
                          type="text"
                          value={newFeatureDescription}
                          onChange={(e) => setNewFeatureDescription(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (newFeature.trim()) {
                                setAddPolicyForm({
                                  ...addPolicyForm,
                                  key_features: [
                                    ...addPolicyForm.key_features,
                                    { header: newFeature.trim(), description: newFeatureDescription.trim() },
                                  ],
                                });
                                setNewFeature('');
                                setNewFeatureDescription('');
                              }
                            }
                          }}
                          placeholder="Description (optional)"
                          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (newFeature.trim()) {
                            setAddPolicyForm({
                              ...addPolicyForm,
                              key_features: [
                                ...addPolicyForm.key_features,
                                { header: newFeature.trim(), description: newFeatureDescription.trim() },
                              ],
                            });
                            setNewFeature('');
                            setNewFeatureDescription('');
                          }
                        }}
                        disabled={!newFeature.trim()}
                        className="mt-2 px-3 py-2 text-sm font-medium text-green-600 dark:text-green-400 border border-green-300 dark:border-green-700 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add feature
                      </button>
                    </div>
                  </div>
                )}

                {/* Premiums Section */}
                {policyFormSection === 'premiums' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min Premium</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={addPolicyForm.min_premium}
                          onChange={(e) => setAddPolicyForm({ ...addPolicyForm, min_premium: e.target.value })}
                          placeholder="0.00"
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Premium</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={addPolicyForm.max_premium}
                          onChange={(e) => setAddPolicyForm({ ...addPolicyForm, max_premium: e.target.value })}
                          placeholder="0.00"
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                    <fieldset>
                      <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Premium Payment Modes</legend>
                      <div className="flex flex-wrap gap-4">
                        {PREMIUM_FREQUENCY_OPTIONS.map((option) => (
                          <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={addPolicyForm.premium_payment_modes.includes(option.value)}
                              onChange={(e) =>
                                setAddPolicyForm({
                                  ...addPolicyForm,
                                  premium_payment_modes: e.target.checked
                                    ? [...addPolicyForm.premium_payment_modes, option.value]
                                    : addPolicyForm.premium_payment_modes.filter((m) => m !== option.value),
                                })
                              }
                              className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <MappingField label="Tenure Min (months)" value={addPolicyForm.tenure_min_months}
                        inherited={catalogueDefaults.tenure_min_months}
                        onChange={(value) => setAddPolicyForm({ ...addPolicyForm, tenure_min_months: value })} />
                      <MappingField label="Tenure Max (months)" value={addPolicyForm.tenure_max_months}
                        inherited={catalogueDefaults.tenure_max_months}
                        onChange={(value) => setAddPolicyForm({ ...addPolicyForm, tenure_max_months: value })} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sum Insured (Min)</label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={addPolicyForm.sum_insured_min}
                          onChange={(e) => setAddPolicyForm({ ...addPolicyForm, sum_insured_min: e.target.value })}
                          placeholder="e.g. 100000"
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sum Insured (Max)</label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={addPolicyForm.sum_insured_max}
                          onChange={(e) => setAddPolicyForm({ ...addPolicyForm, sum_insured_max: e.target.value })}
                          placeholder="e.g. 5000000"
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Eligibility Section */}
                {policyFormSection === 'eligibility' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min Age</label>
                        <input
                          type="number"
                          min="0"
                          max="150"
                          value={addPolicyForm.min_age}
                          onChange={(e) => setAddPolicyForm({ ...addPolicyForm, min_age: e.target.value })}
                          placeholder="e.g. 18"
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Age</label>
                        <input
                          type="number"
                          min="0"
                          max="150"
                          value={addPolicyForm.max_age}
                          onChange={(e) => setAddPolicyForm({ ...addPolicyForm, max_age: e.target.value })}
                          placeholder="e.g. 65"
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Waiting Period</label>
                      <input
                        type="text"
                        value={addPolicyForm.waiting_period}
                        onChange={(e) => setAddPolicyForm({ ...addPolicyForm, waiting_period: e.target.value })}
                        placeholder="e.g. 30 days for pre-existing conditions"
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <MappingField label="Max Renewal Age" value={addPolicyForm.max_renewal_age}
                      onChange={(value) => setAddPolicyForm({ ...addPolicyForm, max_renewal_age: value })} />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <MappingField label="Initial Waiting (days)" value={addPolicyForm.waiting_initial_days}
                        onChange={(value) => setAddPolicyForm({ ...addPolicyForm, waiting_initial_days: value })} />
                      <MappingField label="Specific Illness Waiting (months)" value={addPolicyForm.waiting_specific_months}
                        onChange={(value) => setAddPolicyForm({ ...addPolicyForm, waiting_specific_months: value })} />
                      <MappingField label="Pre-existing Waiting (months)" value={addPolicyForm.waiting_ped_months}
                        onChange={(value) => setAddPolicyForm({ ...addPolicyForm, waiting_ped_months: value })} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <MappingField label="Free-look Period (days)" min={15} value={addPolicyForm.free_look_days} placeholder="Minimum 15"
                        onChange={(value) => setAddPolicyForm({ ...addPolicyForm, free_look_days: value })} />
                      <MappingField label="Grace Period (days)" value={addPolicyForm.grace_period_days}
                        inherited={catalogueDefaults.grace_period_days}
                        onChange={(value) => setAddPolicyForm({ ...addPolicyForm, grace_period_days: value })} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Eligibility Notes</label>
                      <textarea
                        value={addPolicyForm.eligibility_notes}
                        onChange={(e) => setAddPolicyForm({ ...addPolicyForm, eligibility_notes: e.target.value })}
                        placeholder="Any additional eligibility criteria"
                        rows={3}
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                )}

                {/* Documents & Links Section */}
                {policyFormSection === 'documents' && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Upload Documents</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Brochure PDF</label>
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setAddPolicyForm({ ...addPolicyForm, brochure_pdf: e.target.files?.[0] || null })}
                            className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-300 dark:file:border-gray-600 file:text-sm file:font-medium file:bg-white dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-300 hover:file:bg-gray-50 dark:hover:file:bg-gray-600"
                          />
                          {existingPolicyDocs.brochure_pdf && !addPolicyForm.brochure_pdf && !removePolicyDocs.brochure_pdf && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Current:{' '}
                              <a href={existingPolicyDocs.brochure_pdf} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">
                                {decodeURIComponent(existingPolicyDocs.brochure_pdf.split('/').pop() || 'Brochure PDF')}
                              </a>
                              {' '}&mdash; kept unless you choose a new file{' '}
                              <button
                                type="button"
                                onClick={() => setRemovePolicyDocs({ ...removePolicyDocs, brochure_pdf: true })}
                                className="text-red-600 dark:text-red-400 hover:underline font-medium"
                              >
                                Remove
                              </button>
                            </p>
                          )}
                          {removePolicyDocs.brochure_pdf && !addPolicyForm.brochure_pdf && (
                            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                              Brochure PDF will be removed when you save{' '}
                              <button
                                type="button"
                                onClick={() => setRemovePolicyDocs({ ...removePolicyDocs, brochure_pdf: false })}
                                className="text-gray-600 dark:text-gray-300 hover:underline font-medium"
                              >
                                Undo
                              </button>
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Policy Wording PDF</label>
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setAddPolicyForm({ ...addPolicyForm, policy_wording_pdf: e.target.files?.[0] || null })}
                            className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-300 dark:file:border-gray-600 file:text-sm file:font-medium file:bg-white dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-300 hover:file:bg-gray-50 dark:hover:file:bg-gray-600"
                          />
                          {existingPolicyDocs.policy_wording_pdf && !addPolicyForm.policy_wording_pdf && !removePolicyDocs.policy_wording_pdf && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Current:{' '}
                              <a href={existingPolicyDocs.policy_wording_pdf} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">
                                {decodeURIComponent(existingPolicyDocs.policy_wording_pdf.split('/').pop() || 'Policy Wording PDF')}
                              </a>
                              {' '}&mdash; kept unless you choose a new file{' '}
                              <button
                                type="button"
                                onClick={() => setRemovePolicyDocs({ ...removePolicyDocs, policy_wording_pdf: true })}
                                className="text-red-600 dark:text-red-400 hover:underline font-medium"
                              >
                                Remove
                              </button>
                            </p>
                          )}
                          {removePolicyDocs.policy_wording_pdf && !addPolicyForm.policy_wording_pdf && (
                            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                              Policy Wording PDF will be removed when you save{' '}
                              <button
                                type="button"
                                onClick={() => setRemovePolicyDocs({ ...removePolicyDocs, policy_wording_pdf: false })}
                                className="text-gray-600 dark:text-gray-300 hover:underline font-medium"
                              >
                                Undo
                              </button>
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Claim Form PDF</label>
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setAddPolicyForm({ ...addPolicyForm, claim_form_pdf: e.target.files?.[0] || null })}
                            className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-300 dark:file:border-gray-600 file:text-sm file:font-medium file:bg-white dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-300 hover:file:bg-gray-50 dark:hover:file:bg-gray-600"
                          />
                          {existingPolicyDocs.claim_form_pdf && !addPolicyForm.claim_form_pdf && !removePolicyDocs.claim_form_pdf && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Current:{' '}
                              <a href={existingPolicyDocs.claim_form_pdf} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">
                                {decodeURIComponent(existingPolicyDocs.claim_form_pdf.split('/').pop() || 'Claim Form PDF')}
                              </a>
                              {' '}&mdash; kept unless you choose a new file{' '}
                              <button
                                type="button"
                                onClick={() => setRemovePolicyDocs({ ...removePolicyDocs, claim_form_pdf: true })}
                                className="text-red-600 dark:text-red-400 hover:underline font-medium"
                              >
                                Remove
                              </button>
                            </p>
                          )}
                          {removePolicyDocs.claim_form_pdf && !addPolicyForm.claim_form_pdf && (
                            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                              Claim Form PDF will be removed when you save{' '}
                              <button
                                type="button"
                                onClick={() => setRemovePolicyDocs({ ...removePolicyDocs, claim_form_pdf: false })}
                                className="text-gray-600 dark:text-gray-300 hover:underline font-medium"
                              >
                                Undo
                              </button>
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sample Policy PDF</label>
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setAddPolicyForm({ ...addPolicyForm, sample_policy_pdf: e.target.files?.[0] || null })}
                            className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-300 dark:file:border-gray-600 file:text-sm file:font-medium file:bg-white dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-300 hover:file:bg-gray-50 dark:hover:file:bg-gray-600"
                          />
                          {existingPolicyDocs.sample_policy_pdf && !addPolicyForm.sample_policy_pdf && !removePolicyDocs.sample_policy_pdf && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Current:{' '}
                              <a href={existingPolicyDocs.sample_policy_pdf} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">
                                {decodeURIComponent(existingPolicyDocs.sample_policy_pdf.split('/').pop() || 'Sample Policy PDF')}
                              </a>
                              {' '}&mdash; kept unless you choose a new file{' '}
                              <button
                                type="button"
                                onClick={() => setRemovePolicyDocs({ ...removePolicyDocs, sample_policy_pdf: true })}
                                className="text-red-600 dark:text-red-400 hover:underline font-medium"
                              >
                                Remove
                              </button>
                            </p>
                          )}
                          {removePolicyDocs.sample_policy_pdf && !addPolicyForm.sample_policy_pdf && (
                            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                              Sample Policy PDF will be removed when you save{' '}
                              <button
                                type="button"
                                onClick={() => setRemovePolicyDocs({ ...removePolicyDocs, sample_policy_pdf: false })}
                                className="text-gray-600 dark:text-gray-300 hover:underline font-medium"
                              >
                                Undo
                              </button>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">External Links</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Product Page URL</label>
                          <input
                            type="url"
                            value={addPolicyForm.product_page_url}
                            onChange={(e) => setAddPolicyForm({ ...addPolicyForm, product_page_url: e.target.value })}
                            placeholder="https://..."
                            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Claim Process URL</label>
                          <input
                            type="url"
                            value={addPolicyForm.claim_process_url}
                            onChange={(e) => setAddPolicyForm({ ...addPolicyForm, claim_process_url: e.target.value })}
                            placeholder="https://..."
                            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Terms & Conditions URL</label>
                          <input
                            type="url"
                            value={addPolicyForm.terms_url}
                            onChange={(e) => setAddPolicyForm({ ...addPolicyForm, terms_url: e.target.value })}
                            placeholder="https://..."
                            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Settings Section */}
                {policyFormSection === 'settings' && (
                  <div className="space-y-4">
                    <MappingField label="Launch Date" type="date" value={addPolicyForm.launch_date}
                      inherited={catalogueDefaults.launch_date}
                      onChange={(value) => setAddPolicyForm({ ...addPolicyForm, launch_date: value })} />
                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={addPolicyForm.is_active}
                          onChange={(e) => setAddPolicyForm({ ...addPolicyForm, is_active: e.target.checked })}
                          className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                      </label>
                    </div>
                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={addPolicyForm.is_visible_to_all}
                          onChange={(e) => setAddPolicyForm({ ...addPolicyForm, is_visible_to_all: e.target.checked })}
                          className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Visible to all brokers</span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tags <span className="text-xs text-gray-400">(comma-separated)</span>
                      </label>
                      <input
                        type="text"
                        value={addPolicyForm.tags}
                        onChange={(e) => setAddPolicyForm({ ...addPolicyForm, tags: e.target.value })}
                        placeholder="e.g. health, family, top-seller"
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      {addPolicyForm.tags.trim() && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {addPolicyForm.tags.split(',').map((t) => t.trim()).filter(Boolean).map((tag, i) => (
                            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Footer buttons */}
                <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex gap-2">
                    {policyFormSection !== 'basic' && (
                      <button
                        type="button"
                        onClick={() => {
                          const sections: PolicyFormSection[] = ['basic', 'details', 'premiums', 'eligibility', 'documents', 'settings'];
                          const idx = sections.indexOf(policyFormSection);
                          if (idx > 0) setPolicyFormSection(sections[idx - 1]);
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        Previous
                      </button>
                    )}
                    {policyFormSection !== 'settings' && (
                      <button
                        type="button"
                        onClick={() => {
                          const sections: PolicyFormSection[] = ['basic', 'details', 'premiums', 'eligibility', 'documents', 'settings'];
                          const idx = sections.indexOf(policyFormSection);
                          if (idx < sections.length - 1) setPolicyFormSection(sections[idx + 1]);
                        }}
                        className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-white dark:bg-gray-800 border border-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                      >
                        Next
                      </button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => { setShowAddPolicyModal(false); setEditingPolicyId(null); setPolicyFormSection('basic'); }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                      {submitting && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      )}
                      {editingPolicyId ? 'Update Product Mapping' : 'Add Product Mapping'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Broker Access Modal */}
      {brokerAccessPolicy && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setBrokerAccessPolicy(null)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Broker Access - {brokerAccessPolicy.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {brokerAccessList.filter(b => b.access_status === 'APPROVED').length} of {brokerAccessList.length} brokers have access
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleGrantAll}
                    disabled={togglingBrokerId === 'all' || brokerAccessList.filter(b => b.access_status === 'NO_ACCESS').length === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {togglingBrokerId === 'all' && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700" />
                    )}
                    Grant All
                  </button>
                  <button
                    onClick={() => setBrokerAccessPolicy(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {brokerAccessLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                </div>
              ) : brokerAccessList.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Broker Name</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Code</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">City</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Status</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {brokerAccessList.map((broker) => (
                        <tr key={broker.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-2 font-medium text-gray-900 dark:text-gray-100">{broker.name}</td>
                          <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{broker.code}</td>
                          <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{broker.city || '-'}</td>
                          <td className="px-4 py-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                broker.access_status === 'APPROVED'
                                  ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                  : broker.access_status === 'NO_ACCESS'
                                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                  : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                              }`}
                            >
                              {broker.access_status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            {broker.access_status === 'APPROVED' ? (
                              <button
                                onClick={() => handleToggleBrokerAccess(broker.id, false)}
                                disabled={togglingBrokerId === broker.id}
                                className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                              >
                                {togglingBrokerId === broker.id && (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-700" />
                                )}
                                Revoke
                              </button>
                            ) : broker.access_status === 'NO_ACCESS' ? (
                              <button
                                onClick={() => handleToggleBrokerAccess(broker.id, true)}
                                disabled={togglingBrokerId === broker.id}
                                className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50"
                              >
                                {togglingBrokerId === broker.id && (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-700" />
                                )}
                                Grant
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">N/A</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6">
                  <UsersIcon className="w-8 h-8 mx-auto text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No brokers found for this policy.</p>
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setBrokerAccessPolicy(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AssignBrokerModal({
  brokers,
  onAssign,
  onClose,
  submitting,
}: {
  brokers: BrokerRelation[];
  onAssign: (brokerIds: string[]) => void;
  onClose: () => void;
  submitting: boolean;
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Item 3e: the modal's candidate list is already fetched in full (every
  // unassigned broker in the insurer's country), so search is a client-side
  // filter — no new endpoint or pagination needed for what is typically a few
  // dozen rows.
  const [search, setSearch] = useState('');

  const toggleBroker = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const term = search.trim().toLowerCase();
  const filteredBrokers = term
    ? brokers.filter(
        (broker) => broker.name.toLowerCase().includes(term) || broker.code.toLowerCase().includes(term)
      )
    : brokers;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Assign Brokers</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <span className="sr-only">Close</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          {brokers.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-4">All brokers are already assigned to this insurer.</p>
          ) : (
            <>
              <div className="relative mb-3">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by broker name or code"
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              {filteredBrokers.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4">No brokers match &quot;{search}&quot;.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto space-y-2">
                  {filteredBrokers.map((broker) => (
                    <label
                      key={broker.id}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer border border-gray-200 dark:border-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(broker.id)}
                        onChange={() => toggleBroker(broker.id)}
                        className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{broker.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{broker.code}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onAssign(Array.from(selectedIds))}
              disabled={submitting || selectedIds.size === 0}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {submitting && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              )}
              Assign ({selectedIds.size})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
